// Types + TanStack Query hooks for the Insider Trading domain.
//
// Uses the shared authenticated Axios instance (`apiClient`, baseURL already
// includes `/api/v1`), so paths are the BARE `/insider-trading/...` routes.
// Field names are snake_case, matching the backend Pydantic contracts.

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiClient } from '../../../api/client';

const BASE_URL = '/insider-trading';

export type InsiderTransactionType = 'P-Purchase' | 'S-Sale';

export interface InsiderTrade {
    symbol: string;
    filing_date: string | null;
    transaction_date: string | null;
    reporting_cik: string | null;
    company_cik: string | null;
    transaction_type: string | null;
    securities_owned: number | null;
    reporting_name: string | null;
    type_of_owner: string | null;
    acquisition_or_disposition: string | null;
    direct_or_indirect: string | null;
    form_type: string | null;
    securities_transacted: number | null;
    price: number | null;
    transaction_value: number | null;
    security_name: string | null;
    url: string | null;
}

export interface TopLiquidInsiderRow {
    rank: number;
    symbol: string;
    name: string | null;
    exchange: string | null;
    transaction_type: InsiderTransactionType;
    latest_filing_date: string | null;
    latest_transaction_date: string | null;
    trade_count: number;
    insider_count: number;
    total_shares: number;
    total_value: number;
    vwap: number | null;
    last_price: number | null;
    price_vs_vwap_pct: number | null;
    avg_volume: number | null;
    dollar_volume: number | null;
    market_cap: number | null;
    value_to_adv_pct: number | null;
    value_to_mcap_bps: number | null;
    top_insiders: string[];
    trades: InsiderTrade[];
}

export interface TopLiquidInsiderResponse {
    purchases: TopLiquidInsiderRow[];
    sales: TopLiquidInsiderRow[];
    window_from: string | null;
    window_to: string | null;
    latest_available_filing_date: string | null;
    purchase_symbols_considered: number;
    sale_symbols_considered: number;
    generated_at: string;
    execution_time_ms: number;
}

export interface InsiderSearchResponse {
    items: InsiderTrade[];
    page: number;
    limit: number;
    count: number;
}

export interface InsiderStatisticRow {
    symbol: string;
    cik: string | null;
    year: number;
    quarter: number;
    acquired_transactions: number | null;
    disposed_transactions: number | null;
    acquired_disposed_ratio: number | null;
    total_acquired: number | null;
    total_disposed: number | null;
    average_acquired: number | null;
    average_disposed: number | null;
    total_purchases: number | null;
    total_sales: number | null;
}

export interface InsiderStatisticsResponse {
    symbol: string;
    items: InsiderStatisticRow[];
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------
export const fetchTopLiquidInsiders = async (
    days: number,
    filingDate?: string,
    limit = 20,
): Promise<TopLiquidInsiderResponse> => {
    const { data } = await apiClient.get<TopLiquidInsiderResponse>(`${BASE_URL}/top-liquid`, {
        params: { days, limit, filing_date: filingDate || undefined },
    });
    return data;
};

export const fetchInsiderStatistics = async (
    symbol: string,
): Promise<InsiderStatisticsResponse> => {
    const { data } = await apiClient.get<InsiderStatisticsResponse>(
        `${BASE_URL}/statistics/${encodeURIComponent(symbol)}`,
    );
    return data;
};

export const fetchInsiderSearch = async (params: {
    symbol?: string;
    transactionType?: string;
    page?: number;
    limit?: number;
}): Promise<InsiderSearchResponse> => {
    const { data } = await apiClient.get<InsiderSearchResponse>(`${BASE_URL}/search`, {
        params: {
            symbol: params.symbol || undefined,
            transaction_type: params.transactionType || undefined,
            page: params.page ?? 0,
            limit: params.limit ?? 50,
        },
    });
    return data;
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
export const useTopLiquidInsiders = (days: number, filingDate?: string) =>
    useQuery({
        queryKey: ['insider-trading', 'top-liquid', days, filingDate || 'window'],
        queryFn: () => fetchTopLiquidInsiders(days, filingDate),
        // The worker refreshes twice daily; 5 minutes is plenty fresh.
        staleTime: 5 * 60 * 1000,
        placeholderData: keepPreviousData,
    });

export const useInsiderStatistics = (symbol: string | null) =>
    useQuery({
        queryKey: ['insider-trading', 'statistics', symbol],
        queryFn: () => fetchInsiderStatistics(symbol as string),
        enabled: !!symbol,
        staleTime: 60 * 60 * 1000,
    });

export const useInsiderSearch = (symbol: string | null, transactionType?: string) =>
    useQuery({
        queryKey: ['insider-trading', 'search', symbol, transactionType || 'all'],
        queryFn: () => fetchInsiderSearch({ symbol: symbol as string, transactionType, limit: 50 }),
        enabled: !!symbol,
        staleTime: 10 * 60 * 1000,
    });
